/**
 * Feature Flags Configuration
 *
 * Provides centralized control over feature availability for quick rollback
 * and phased rollouts. Flags default to enabled for production use.
 */

interface FeatureFlags {
  /** Phase 1A: Prediction Metrics UI (Accuracy + Effect Size) */
  metrics_ui_prediction: boolean;

  /** Legacy Financial Metrics (for comparison/migration) */
  metrics_ui_financial: boolean;

  /** Debug mode for development (shows internal state) */
  debug_show_sample_sizes: boolean;
}

/**
 * Default feature flags configuration.
 * Override with environment variables for different deployment stages.
 */
const DEFAULT_FEATURE_FLAGS: FeatureFlags = {
  metrics_ui_prediction: true,     // Phase 1A: Enabled by default
  metrics_ui_financial: false,     // Legacy: Disabled in favor of prediction metrics
  debug_show_sample_sizes: false,  // Debug: Disabled in production
};

/**
 * Get feature flag value from environment variable or default.
 *
 * Environment variable format: VITE_FEATURE_FLAG_NAME
 * Example: VITE_METRICS_UI_PREDICTION=false
 */
function getFeatureFlag<K extends keyof FeatureFlags>(
  flagName: K,
  defaultValue: FeatureFlags[K]
): FeatureFlags[K] {
  const envVar = `VITE_${flagName.toUpperCase()}`;
  const envValue = import.meta.env[envVar];

  if (envValue !== undefined) {
    // Parse boolean from environment variable
    if (typeof defaultValue === 'boolean') {
      return (envValue.toLowerCase() === 'true') as FeatureFlags[K];
    }
  }

  return defaultValue;
}

/**
 * Exported feature flags with environment override support.
 *
 * Usage examples:
 *
 * // In .env.local for development:
 * VITE_METRICS_UI_PREDICTION=false
 * VITE_DEBUG_SHOW_SAMPLE_SIZES=true
 *
 * // In .env.production for production:
 * VITE_METRICS_UI_PREDICTION=true
 */
export const FEATURE_FLAGS: FeatureFlags = {
  metrics_ui_prediction: getFeatureFlag('metrics_ui_prediction', DEFAULT_FEATURE_FLAGS.metrics_ui_prediction),
  metrics_ui_financial: getFeatureFlag('metrics_ui_financial', DEFAULT_FEATURE_FLAGS.metrics_ui_financial),
  debug_show_sample_sizes: getFeatureFlag('debug_show_sample_sizes', DEFAULT_FEATURE_FLAGS.debug_show_sample_sizes),
};

/**
 * Convenience function to check if prediction metrics are enabled.
 * This is the primary flag for Phase 1A functionality.
 */
export function isPredictionMetricsEnabled(): boolean {
  return FEATURE_FLAGS.metrics_ui_prediction;
}

/**
 * Debug helper to show current flag configuration in development.
 */
export function getDebugInfo(): Record<string, any> {
  if (import.meta.env.MODE === 'development' || FEATURE_FLAGS.debug_show_sample_sizes) {
    return {
      environment: import.meta.env.MODE,
      flags: FEATURE_FLAGS,
      sources: Object.entries(DEFAULT_FEATURE_FLAGS).reduce((acc, [key, defaultValue]) => {
        const envVar = `VITE_${key.toUpperCase()}`;
        const envValue = import.meta.env[envVar];
        acc[key] = {
          value: FEATURE_FLAGS[key as keyof FeatureFlags],
          default: defaultValue,
          fromEnv: envValue !== undefined ? envValue : 'unset'
        };
        return acc;
      }, {} as Record<string, any>)
    };
  }
  return {} as Record<string, any>;
}